import { findCountry } from './countries';
import type {
  CountryIsoCode,
  CurrencyIsoCode,
  DatePattern,
  IanaTimeZone,
  LanguageIsoCode,
  TimePattern,
} from './types';

/**
 * Five regional fields a country can suggest defaults for. Apps may extend
 * with their own form keys — this just defines the shape the helper writes.
 */
export interface RegionalFields {
  languageCode: LanguageIsoCode;
  currencyIsoCode: CurrencyIsoCode;
  operationalTimeZone: IanaTimeZone;
  datePattern: DatePattern;
  timePattern: TimePattern;
}

export interface ApplyCountryDefaultsArgs {
  /** Newly selected country ISO 3166-1 alpha-2 code. */
  newCountry: CountryIsoCode;
  /**
   * Country selected before the current change. `null` means this is the
   * first selection (or the page just loaded). The helper uses this to
   * decide whether each field still looks "auto-applied" — fields equal to
   * the previous country's defaults are treated as untouched.
   */
  previousCountry: CountryIsoCode | null;
  /** Current form values (only the fields the cascade can touch). */
  current: Partial<RegionalFields>;
}

/**
 * Compute a partial patch of regional fields when the country changes.
 *
 * Per-field rule:
 *  - If the field is empty → patch with the new country's default.
 *  - If the field equals the *previous* country's default for that field →
 *    treat as auto-applied, swap to the new country's default.
 *  - Otherwise → leave alone (it's an explicit user override, never overwrite).
 *
 * Returns a partial object; spread it into form state. Empty object means
 * nothing to patch.
 */
export function applyCountryDefaults({
  newCountry,
  previousCountry,
  current,
}: ApplyCountryDefaultsArgs): Partial<RegionalFields> {
  const next = findCountry(newCountry);
  if (!next) return {};

  const prev = previousCountry ? findCountry(previousCountry) : null;
  const patch: Partial<RegionalFields> = {};

  const tryField = <K extends keyof RegionalFields>(
    key: K,
    nextValue: RegionalFields[K],
    prevValue: RegionalFields[K] | undefined,
  ) => {
    const currentValue = current[key];
    if (currentValue === undefined || currentValue === '' || currentValue === prevValue) {
      if (currentValue !== nextValue) patch[key] = nextValue;
    }
  };

  tryField('languageCode', next.defaultLanguageCode, prev?.defaultLanguageCode);
  tryField('currencyIsoCode', next.defaultCurrencyIsoCode, prev?.defaultCurrencyIsoCode);
  tryField('operationalTimeZone', next.defaultTimeZone, prev?.defaultTimeZone);
  tryField('datePattern', next.defaultDatePattern, prev?.defaultDatePattern);
  tryField('timePattern', next.defaultTimePattern, prev?.defaultTimePattern);

  return patch;
}
